import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { Activity, TrendingUp, FlaskConical, BarChart3, FileDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { SubcategoryChart } from "./SubcategoryChart";
import { PatientIndicatorsPDFWrapper } from "./PatientIndicatorsPDFWrapper";
import { useExportToPDF } from "@/hooks/useExportToPDF";
// Removido: Componentes customizados não são mais necessários
// Todos os tipos agora usam SubcategoryChart através de lab_results_onboarding

interface PatientHealthIndicatorsProps {
  patientId: string;
  hideHeader?: boolean;
  period?: Period;
  pdfExportRef?: React.MutableRefObject<{ exportPDF: () => void } | null>;
}

interface Subcategory {
  id: string;
  name: string;
  category_id: string;
  category_name: string;
}

interface LabResultWithSubcategory {
  id: string;
  test_name: string;
  value: number | null;
  unit: string | null;
  ref_low: number | null;
  ref_high: number | null;
  collected_at: string | null;
  created_at: string;
  subcategory_id: string | null;
  subcategory_name: string | null;
  category_name: string | null;
  source_session_id: string | null;
  _isPair?: boolean;
  _side?: 'direita' | 'esquerda';
  _isBloodPressure?: boolean;
  _bpSide?: 'sistolica' | 'diastolica';
}

export type Period = "7" | "30" | "90" | "365" | "all";

export const PatientHealthIndicators = ({
  patientId,
  hideHeader = false,
  period: externalPeriod,
  pdfExportRef
}: PatientHealthIndicatorsProps) => {
  const navigate = useNavigate();
  const [labResults, setLabResults] = useState<LabResultWithSubcategory[]>([]);
  const [enabledSubcategories, setEnabledSubcategories] = useState<Subcategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [internalPeriod, setInternalPeriod] = useState<Period>("all");

  const [patient, setPatient] = useState<any>(null);
  const [professional, setProfessional] = useState<any>(null);

  // Removido: Estados customizados não são mais necessários
  // Todos os dados agora vêm de lab_results_onboarding e são exibidos via SubcategoryChart

  const activePeriod = externalPeriod || internalPeriod;

  // Hook para exportação de PDF com preparação correta de SVGs
  const pdfContentRef = useRef<HTMLDivElement | null>(null);
  const { exportToPDF, isExporting } = useExportToPDF(
    pdfContentRef,
    patient
      ? `relatorio-${patient.full_name}-${new Date().toISOString().split('T')[0]}.pdf`
      : "relatorio-indicadores.pdf"
  );

  const handleExportPDF = async () => {
    if (!patient || !professional) {
      toast({
        title: "Aguarde",
        description: "Carregando dados para exportação...",
      });
      return;
    }

    // Delega ao hook que já cuida de preparar SVGs e configurar html2pdf
    await exportToPDF();
  };

  // Expor função de exportação via ref
  useEffect(() => {
    if (pdfExportRef) {
      pdfExportRef.current = {
        exportPDF: handleExportPDF
      };
    }
    return () => {
      if (pdfExportRef) {
        pdfExportRef.current = null;
      }
    };
  }, [pdfExportRef, patient, professional]);

  useEffect(() => {
    loadData();
  }, [patientId]);

  // Removido: loadPhysiotherapyData e loadGoniometryData
  // Todos os dados agora são carregados via loadLabResults de lab_results_onboarding

  // Buscar dados do paciente e profissional para o PDF
  useEffect(() => {
    const loadPatientAndProfessional = async () => {
      try {
        // Buscar paciente
        const { data: patientData } = await supabase
          .from('patients_onboarding')
          .select('full_name, cpf, data_nascimento, celular, email')
          .eq('id', patientId)
          .single();

        setPatient(patientData);

        // Buscar profissional logado
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: professionalData } = await supabase
            .from('profiles')
            .select('full_name, cpf, health_area, phone')
            .eq('id', user.id)
            .single();

          setProfessional(professionalData);
        }
      } catch (error) {
        console.error('Erro ao buscar dados para PDF:', error);
      }
    };

    loadPatientAndProfessional();
  }, [patientId]);

  // Listener para recarregar quando houver atualizações de subcategorias
  useEffect(() => {
    const handleReload = () => {
      loadData();
    };

    window.addEventListener('reload-health-indicators', handleReload);
    return () => window.removeEventListener('reload-health-indicators', handleReload);
  }, [patientId]);

  const loadData = async () => {
    setLoading(true);

    // Garantir que o skeleton apareça por pelo menos 300ms para evitar piscadas
    // Agora todos os dados vêm de lab_results_onboarding (incluindo SPPB, Dinamometria e Goniometria)
    await Promise.all([
      loadChartPreferences(),
      loadLabResults()
      // Removido: loadPhysiotherapyData() e loadGoniometryData()
      // Esses dados agora são carregados via loadLabResults() de lab_results_onboarding
    ]);

    // Aguardar um tempo mínimo para que o skeleton seja visível
    await new Promise(resolve => setTimeout(resolve, 300));

    setLoading(false);
  };

  const loadChartPreferences = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('lab_subcategories')
        .select(`
          id,
          name,
          category_id,
          lab_categories!inner(name)
        `)
        .eq('user_id', user.id)
        .eq('show_chart', true);

      if (error) throw error;

      const subcategories: Subcategory[] = (data || []).map((item: any) => ({
        id: item.id,
        name: item.name,
        category_id: item.category_id,
        category_name: item.lab_categories?.name || 'Sem categoria'
      }));

      setEnabledSubcategories(subcategories);
    } catch (error) {
      console.error("Erro ao carregar preferências de gráficos:", error);
    }
  };

  const loadLabResults = async () => {
    try {
      if (!patientId) return;

      // Buscar resultados com subcategoria vinculada usando patientId diretamente
      // Agora inclui todos os tipos: lab_exam, anthropometry, goniometry, sppb, dynamometry
      const { data, error } = await supabase
        .from("lab_results_onboarding")
        .select(`
          id,
          test_name,
          value,
          unit,
          ref_low,
          ref_high,
          collected_at,
          created_at,
          subcategory_id,
          source_type,
          source_session_id,
          lab_subcategories(
            name,
            category_id,
            lab_categories(name)
          )
        `)
        .eq("patient_id", patientId)
        .not('subcategory_id', 'is', null)
        .order("collected_at", { ascending: true });

      if (error) throw error;

      const results: LabResultWithSubcategory[] = (data || []).map((item: any) => ({
        id: item.id,
        test_name: item.test_name,
        value: item.value,
        unit: item.unit,
        ref_low: item.ref_low,
        ref_high: item.ref_high,
        collected_at: item.collected_at,
        created_at: item.created_at,
        subcategory_id: item.subcategory_id,
        subcategory_name: item.lab_subcategories?.name || null,
        category_name: item.lab_subcategories?.lab_categories?.name || null,
        source_session_id: item.source_session_id || null
      }));

      setLabResults(results);
    } catch (error) {
      console.error("Erro ao carregar resultados:", error);
    }
  };

  // REMOVIDO: loadPhysiotherapyData e loadGoniometryData
  // SPPB, Dinamometria e Goniometria agora são sincronizados para lab_results_onboarding
  // e exibidos via SubcategoryChart como os outros documentos
  // Os dados são carregados automaticamente via loadLabResults()

  // Função auxiliar para extrair o nome base e o lado (Direita/Esquerda)
  // Suporta tanto formato com hífen (-) quanto com parênteses (())
  const extractTestNameInfo = (testName: string): { baseName: string; side: 'direita' | 'esquerda' | null } => {
    // Primeiro, tentar formato com hífen: "Nome - Direita" ou "Nome - Esquerda"
    const direitaMatch = testName.match(/^(.+?)\s*-\s*Direita$/i);
    if (direitaMatch) {
      return { baseName: direitaMatch[1].trim(), side: 'direita' };
    }
    const esquerdaMatch = testName.match(/^(.+?)\s*-\s*Esquerda$/i);
    if (esquerdaMatch) {
      return { baseName: esquerdaMatch[1].trim(), side: 'esquerda' };
    }
    
    // Depois, tentar formato com parênteses: "Nome (direita)" ou "Nome (esquerda)"
    const direitaParenMatch = testName.match(/^(.+?)\s*\(direita\)$/i);
    if (direitaParenMatch) {
      return { baseName: direitaParenMatch[1].trim(), side: 'direita' };
    }
    const esquerdaParenMatch = testName.match(/^(.+?)\s*\(esquerda\)$/i);
    if (esquerdaParenMatch) {
      return { baseName: esquerdaParenMatch[1].trim(), side: 'esquerda' };
    }
    
    return { baseName: testName, side: null };
  };

  // Detectar se é um registro de pressão arterial (Sistólica/Diastólica)
  const extractBloodPressureInfo = (testName: string): { isBloodPressure: boolean; bpSide: 'sistolica' | 'diastolica' | null } => {
    const lower = testName.toLowerCase();
    if (lower.includes('sistólica') || lower.includes('sistolica')) {
      return { isBloodPressure: true, bpSide: 'sistolica' };
    }
    if (lower.includes('diastólica') || lower.includes('diastolica')) {
      return { isBloodPressure: true, bpSide: 'diastolica' };
    }
    return { isBloodPressure: false, bpSide: null };
  };

  // Agrupar resultados considerando pares Direita/Esquerda e Sistólica/Diastólica
  const groupResultsBySubcategory = (): Record<string, LabResultWithSubcategory[]> => {
    const grouped: Record<string, LabResultWithSubcategory[]> = {};
    
    // Agrupar por baseName apenas (sem considerar source_session_id) para juntar todos os dados do mesmo tipo
    const pairedResultsByBaseName = new Map<string, {
      direita: LabResultWithSubcategory[];
      esquerda: LabResultWithSubcategory[];
      subcategory_id: string;
      baseName: string;
    }>();

    // Agrupar Sistólica/Diastólica por subcategory_id
    const bpResultsBySubcategory = new Map<string, {
      sistolica: LabResultWithSubcategory[];
      diastolica: LabResultWithSubcategory[];
      subcategory_id: string;
    }>();

    // Primeiro, processar todos os resultados para identificar pares
    labResults.forEach(result => {
      if (!result.subcategory_id) return;
      
      // Verificar se é pressão arterial
      const { isBloodPressure, bpSide } = extractBloodPressureInfo(result.test_name);
      if (isBloodPressure && bpSide) {
        if (!bpResultsBySubcategory.has(result.subcategory_id)) {
          bpResultsBySubcategory.set(result.subcategory_id, {
            sistolica: [],
            diastolica: [],
            subcategory_id: result.subcategory_id,
          });
        }
        const bp = bpResultsBySubcategory.get(result.subcategory_id)!;
        if (bpSide === 'sistolica') {
          bp.sistolica.push(result);
        } else {
          bp.diastolica.push(result);
        }
        return;
      }
      
      // Usar subcategory_name como fallback (goniometria pode ter test_name numérico)
      const nameToParse = result.test_name || result.subcategory_name || "";
      const { baseName, side } = extractTestNameInfo(nameToParse);
      
      if (side) {
        if (!pairedResultsByBaseName.has(baseName)) {
          pairedResultsByBaseName.set(baseName, {
            direita: [],
            esquerda: [],
            subcategory_id: result.subcategory_id,
            baseName: baseName
          });
        }
        
        const pair = pairedResultsByBaseName.get(baseName)!;
        if (side === 'direita') {
          pair.direita.push(result);
        } else {
          pair.esquerda.push(result);
        }
      }
    });

    // Rastrear quais resultados estão em pares/grupos processados
    const processedResultIds = new Set<string>();

    // Processar pares de pressão arterial (Sistólica/Diastólica)
    bpResultsBySubcategory.forEach((bp) => {
      const hasSistolica = bp.sistolica.length > 0;
      const hasDiastolica = bp.diastolica.length > 0;
      
      const specialKey = `bp_${bp.subcategory_id}`;
      if (!grouped[specialKey]) {
        grouped[specialKey] = [];
      }
      if (hasSistolica) {
        grouped[specialKey].push(...bp.sistolica.map(r => ({ ...r, _isBloodPressure: true, _bpSide: 'sistolica' as const })));
        bp.sistolica.forEach(r => processedResultIds.add(r.id));
      }
      if (hasDiastolica) {
        grouped[specialKey].push(...bp.diastolica.map(r => ({ ...r, _isBloodPressure: true, _bpSide: 'diastolica' as const })));
        bp.diastolica.forEach(r => processedResultIds.add(r.id));
      }
    });
    
    // Processar pares Direita/Esquerda
    pairedResultsByBaseName.forEach((pair, baseName) => {
      const hasDireita = pair.direita.length > 0;
      const hasEsquerda = pair.esquerda.length > 0;
      
      if (hasDireita && hasEsquerda) {
        const specialKey = `pair_${pair.subcategory_id}|${pair.baseName}`;
        if (!grouped[specialKey]) {
          grouped[specialKey] = [];
        }
        grouped[specialKey].push(...pair.direita.map(r => ({ ...r, _isPair: true, _side: 'direita' as const })));
        grouped[specialKey].push(...pair.esquerda.map(r => ({ ...r, _isPair: true, _side: 'esquerda' as const })));
        
        pair.direita.forEach(r => processedResultIds.add(r.id));
        pair.esquerda.forEach(r => processedResultIds.add(r.id));
      } else {
        if (hasDireita) {
          pair.direita.forEach(r => {
            if (!grouped[r.subcategory_id!]) {
              grouped[r.subcategory_id!] = [];
            }
            grouped[r.subcategory_id!].push(r);
          });
        }
        if (hasEsquerda) {
          pair.esquerda.forEach(r => {
            if (!grouped[r.subcategory_id!]) {
              grouped[r.subcategory_id!] = [];
            }
            grouped[r.subcategory_id!].push(r);
          });
        }
      }
    });

    // Agora processar todos os resultados que NÃO estão em pares/grupos processados
    labResults.forEach(result => {
      if (!result.subcategory_id) return;
      
      if (processedResultIds.has(result.id)) {
        return;
      }
      
      const nameToParse = result.test_name || result.subcategory_name || "";
      const { side } = extractTestNameInfo(nameToParse);
      
      if (!side) {
        if (!grouped[result.subcategory_id]) {
          grouped[result.subcategory_id] = [];
        }
        grouped[result.subcategory_id].push(result);
      }
    });

    return grouped;
  };

  const groupedResults = groupResultsBySubcategory();

  // Calcular estatísticas apenas para o PDF (não exibidas na página)
  const calculateStatsForPDF = () => {
    // Filtrar apenas resultados com valor não nulo e com subcategoria
    const validResults = labResults.filter(
      (r) => r.value !== null && r.subcategory_id !== null
    );

    // Contar subcategorias únicas
    const uniqueSubcategories = new Set(
      validResults.map((r) => r.subcategory_id).filter(Boolean)
    );

    // Função para verificar se está fora da faixa de referência
    const isOutOfRange = (result: LabResultWithSubcategory): boolean => {
      if (result.value === null) return false;
      if (result.ref_low !== null && result.value < result.ref_low) return true;
      if (result.ref_high !== null && result.value > result.ref_high) return true;
      return false;
    };

    // Contar resultados fora do normal
    const outOfRange = validResults.filter(isOutOfRange).length;

    // Contar resultados normais
    const normal = validResults.filter((r) => !isOutOfRange(r)).length;

    return {
      subcategoryCount: uniqueSubcategories.size,
      totalMeasurements: validResults.length,
      outOfRange,
      normal,
    };
  };

  const stats = calculateStatsForPDF();

  // Identificar grupos de pares (Direita/Esquerda) e pressão arterial (Sistólica/Diastólica)
  const getChartGroups = () => {
    const chartGroups: Array<{
      key: string;
      subcategory: Subcategory;
      results: LabResultWithSubcategory[];
      isPair: boolean;
      pairBaseName?: string;
    }> = [];

    const processedGroupKeys = new Set<string>();
    const processedResultIds = new Set<string>();

    // Processar grupos de pressão arterial (bp_subcategoryId)
    Object.keys(groupedResults).forEach(groupKey => {
      if (groupKey.startsWith('bp_')) {
        const results = groupedResults[groupKey] || [];
        if (results.length > 0 && !processedGroupKeys.has(groupKey)) {
          const subcategoryId = groupKey.replace('bp_', '');
          const subcategory = enabledSubcategories.find(s => s.id === subcategoryId);
          if (subcategory) {
            chartGroups.push({
              key: groupKey,
              subcategory: subcategory,
              results,
              isPair: false,
            });
            processedGroupKeys.add(groupKey);
            results.forEach(r => processedResultIds.add(r.id));
          }
        }
      }
    });

    // Processar grupos de pares (pair_subcategoryId|baseName)
    Object.keys(groupedResults).forEach(groupKey => {
      if (groupKey.startsWith('pair_')) {
        const results = groupedResults[groupKey] || [];
        if (results.length > 0 && !processedGroupKeys.has(groupKey)) {
          const withoutPrefix = groupKey.replace('pair_', '');
          const parts = withoutPrefix.split('|');
          if (parts.length === 2) {
            const pairBaseName = parts[1];
            
            const resultSubcategory = results.find(r => 
              r.subcategory_id && enabledSubcategories.some(s => s.id === r.subcategory_id)
            );
            
            if (resultSubcategory && resultSubcategory.subcategory_id) {
              const subcategory = enabledSubcategories.find(s => s.id === resultSubcategory.subcategory_id);
              if (subcategory) {
                chartGroups.push({
                  key: groupKey,
                  subcategory: subcategory,
                  results,
                  isPair: true,
                  pairBaseName
                });
                processedGroupKeys.add(groupKey);
                results.forEach(r => processedResultIds.add(r.id));
              }
            }
          }
        }
      }
    });

    // Processar grupos normais (sem par nem pressão arterial)
    enabledSubcategories.forEach(sub => {
      const normalResults = groupedResults[sub.id] || [];
      if (normalResults.length > 0) {
        const filteredResults = normalResults.filter(r => {
          return !r._isPair && !r._isBloodPressure && !processedResultIds.has(r.id);
        });
        
        if (filteredResults.length > 0) {
          chartGroups.push({
            key: sub.id,
            subcategory: sub,
            results: filteredResults,
            isPair: false
          });
        }
      }
    });

    return chartGroups;
  };

  const chartGroups = getChartGroups();

  if (loading) {
    // Sempre mostra skeleton durante o loading para evitar que a tela fique "mexendo"
    return (
      <div className={hideHeader ? "space-y-0 w-full max-w-full overflow-x-hidden" : "space-y-6 w-full max-w-full overflow-x-hidden"}>
        {!hideHeader && (
          <div className="flex justify-between items-center">
            <Skeleton className="h-8 w-48" />
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => navigate(`/app/pacientes/${patientId}#relatorio-analitico`)}>
                <BarChart3 className="h-4 w-4 mr-2" />
                Analisar Resultados
              </Button>
              <Skeleton className="h-10 w-48" />
            </div>
          </div>
        )}
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i}>
              <CardContent className="pt-6">
                <Skeleton className="h-12 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-32" />
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <Skeleton className="h-48 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Verificar se há dados de fisioterapia disponíveis
  // Todos os dados agora são exibidos via enabledSubcategories
  if (enabledSubcategories.length === 0) {
    return (
      <div className={hideHeader ? "space-y-0 w-full max-w-full overflow-x-hidden" : "space-y-6 w-full max-w-full overflow-x-hidden"}>
        {!hideHeader && (
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Indicadores de Saúde</h2>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => navigate(`/app/pacientes/${patientId}#relatorio-analitico`)}>
                <BarChart3 className="h-4 w-4 mr-2" />
                Analisar Resultados
              </Button>
            </div>
          </div>
        )}
        <Card>
          <CardContent className="py-12 text-center space-y-4">
            <FlaskConical className="h-12 w-12 mx-auto text-muted-foreground mb-4 opacity-50" />
            <p className="text-sm text-muted-foreground mb-2">
              Nenhuma subcategoria configurada para exibir gráficos.
            </p>
            <p className="text-xs text-muted-foreground">
              Vá em <span className="font-semibold">Categorias</span> e marque a opção "Gráfico" nas subcategorias que deseja acompanhar.
            </p>
            <Button
              variant="outline"
              onClick={() => window.location.href = '/app/categorias'}
            >
              Configurar categorias
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (chartGroups.length === 0) {
    return (
      <div className={hideHeader ? "space-y-0 w-full max-w-full overflow-x-hidden" : "space-y-6 w-full max-w-full overflow-x-hidden"}>
        {!hideHeader && (
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Indicadores de Saúde</h2>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => navigate(`/app/pacientes/${patientId}#relatorio-analitico`)}>
                <BarChart3 className="h-4 w-4 mr-2" />
                Analisar Resultados
              </Button>
              <Button variant="outline" size="sm" onClick={() => window.location.href = '/app/categorias'}>
                Configurar categorias
              </Button>
              <Select value={activePeriod} onValueChange={(value) => setInternalPeriod(value as Period)}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Últimos 7 dias</SelectItem>
                  <SelectItem value="30">Últimos 30 dias</SelectItem>
                  <SelectItem value="90">Últimos 90 dias</SelectItem>
                  <SelectItem value="all">Todos os períodos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
        <Card>
          <CardContent className="py-12 text-center space-y-4">
            <FlaskConical className="h-12 w-12 mx-auto text-muted-foreground mb-4 opacity-50" />
            <p className="text-sm text-muted-foreground mb-2">
              Nenhum resultado vinculado às subcategorias com gráfico habilitado.
            </p>
            <p className="text-xs text-muted-foreground">
              Faça upload de documentos com exames ou vincule resultados existentes às subcategorias na aba <span className="font-semibold">Resultados</span>.
            </p>
            <Button
              variant="outline"
              onClick={() => window.location.href = '/app/categorias'}
            >
              Configurar categorias
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={hideHeader ? "space-y-0" : "space-y-6"}>
      <div className="w-full max-w-full overflow-x-hidden space-y-6">
        {/* Layout normal - grid 3 colunas */}
        {!hideHeader && (
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
            <h2 className="text-lg sm:text-xl font-semibold">Indicadores de Saúde</h2>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={handleExportPDF}
                disabled={isExporting}
                className="w-full sm:w-auto bg-gradient-to-r from-primary to-cyan-600 hover:from-primary/90 hover:to-cyan-600/90 text-xs sm:text-sm"
                size="sm"
              >
                <FileDown className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                {isExporting ? "Gerando PDF..." : "Exportar PDF"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/app/pacientes/${patientId}#relatorio-analitico`)}
                className="w-full sm:w-auto text-xs sm:text-sm"
              >
                <BarChart3 className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                <span className="hidden md:inline">Analisar Resultados</span>
                <span className="md:hidden">Analisar</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.location.href = '/app/categorias'}
                className="w-full sm:w-auto text-xs sm:text-sm"
              >
                <span className="hidden md:inline">Configurar categorias</span>
                <span className="md:hidden">Configurar</span>
              </Button>
              <Select value={activePeriod} onValueChange={(value) => setInternalPeriod(value as Period)}>
                <SelectTrigger className="w-full sm:w-40 lg:w-48 text-xs sm:text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Últimos 7 dias</SelectItem>
                  <SelectItem value="30">Últimos 30 dias</SelectItem>
                  <SelectItem value="90">Últimos 90 dias</SelectItem>
                  <SelectItem value="all">Todos os períodos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Grid de Gráficos por Subcategoria */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {chartGroups.map(chartGroup => {
            return (
              <SubcategoryChart
                key={chartGroup.key}
                subcategory={chartGroup.subcategory}
                results={chartGroup.results}
                period={activePeriod}
                pdfMode={false}
                pairBaseName={chartGroup.isPair ? chartGroup.pairBaseName : undefined}
              />
            );
          })}
        </div>

        {/* REMOVIDO: Gráficos customizados de SPPB, Dinamometria e Goniometria */}
        {/* Todos os gráficos agora são exibidos via SubcategoryChart acima */}
      </div>

      {/* Conteúdo PDF sempre renderizado (invisível) */}
      <div
        id="pdf-content-hidden"
        ref={pdfContentRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '210mm',
          padding: '20px',
          backgroundColor: '#ffffff',
          opacity: 0,
          pointerEvents: 'none',
          zIndex: -1
        }}
      >
        <PatientIndicatorsPDFWrapper
          patient={patient}
          professional={professional}
          stats={stats}
        >
          <div className="space-y-8">
            {chartGroups.map((chartGroup) => (
              <div key={chartGroup.key} className="pdf-chart">
                <SubcategoryChart
                  subcategory={chartGroup.subcategory}
                  results={chartGroup.results}
                  period={activePeriod}
                  pdfMode={true}
                  pairBaseName={chartGroup.isPair ? chartGroup.pairBaseName : undefined}
                />
              </div>
            ))}
            {/* REMOVIDO: Gráficos customizados de SPPB, Dinamometria e Goniometria no PDF */}
            {/* Todos os gráficos agora são incluídos automaticamente via SubcategoryChart acima */}
          </div>
        </PatientIndicatorsPDFWrapper>
      </div>
    </div>
  );
};
